import { useState, useCallback } from "react";

/**
 * Hook for client-side image enhancement using Canvas API.
 * Applies brightness, contrast, saturation, and sharpening adjustments.
 * Zero cost — runs entirely in the browser.
 */

interface EnhanceOptions {
  readonly brightness?: number; // -100 to 100
  readonly contrast?: number; // -100 to 100
  readonly saturation?: number; // -100 to 100
  readonly sharpen?: boolean;
}

interface UseImageEnhanceReturn {
  readonly enhance: (
    imageBlob: Blob,
    options?: EnhanceOptions
  ) => Promise<Blob | null>;
  readonly autoEnhance: (imageBlob: Blob) => Promise<Blob | null>;
  readonly isProcessing: boolean;
  readonly error: string | null;
}

const AUTO_ENHANCE_SETTINGS: EnhanceOptions = {
  brightness: 5,
  contrast: 10,
  saturation: 8,
  sharpen: true,
};

function applyEnhancements(
  canvas: HTMLCanvasElement,
  ctx: CanvasRenderingContext2D,
  options: EnhanceOptions
): void {
  const filters: string[] = [];

  if (options.brightness !== undefined && options.brightness !== 0) {
    filters.push(`brightness(${1 + options.brightness / 100})`);
  }
  if (options.contrast !== undefined && options.contrast !== 0) {
    filters.push(`contrast(${1 + options.contrast / 100})`);
  }
  if (options.saturation !== undefined && options.saturation !== 0) {
    filters.push(`saturate(${1 + options.saturation / 100})`);
  }

  if (filters.length > 0) {
    // Draw with CSS filters applied
    const tempCanvas = document.createElement("canvas");
    tempCanvas.width = canvas.width;
    tempCanvas.height = canvas.height;
    const tempCtx = tempCanvas.getContext("2d")!;
    tempCtx.drawImage(canvas, 0, 0);

    ctx.filter = filters.join(" ");
    ctx.drawImage(tempCanvas, 0, 0);
    ctx.filter = "none";
  }

  if (options.sharpen) {
    // Simple unsharp mask approximation using a convolution kernel
    const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
    const data = imageData.data;
    const w = canvas.width;
    const copy = new Uint8ClampedArray(data);

    // 3x3 sharpen kernel
    const kernel = [0, -1, 0, -1, 5, -1, 0, -1, 0];

    for (let y = 1; y < canvas.height - 1; y++) {
      for (let x = 1; x < w - 1; x++) {
        for (let c = 0; c < 3; c++) {
          let val = 0;
          for (let ky = -1; ky <= 1; ky++) {
            for (let kx = -1; kx <= 1; kx++) {
              const idx = ((y + ky) * w + (x + kx)) * 4 + c;
              val += copy[idx] * kernel[(ky + 1) * 3 + (kx + 1)];
            }
          }
          data[(y * w + x) * 4 + c] = Math.max(0, Math.min(255, val));
        }
      }
    }

    ctx.putImageData(imageData, 0, 0);
  }
}

export function useImageEnhance(): UseImageEnhanceReturn {
  const [isProcessing, setIsProcessing] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const enhance = useCallback(
    async (
      imageBlob: Blob,
      options: EnhanceOptions = AUTO_ENHANCE_SETTINGS
    ): Promise<Blob | null> => {
      setIsProcessing(true);
      setError(null);

      try {
        const bitmap = await createImageBitmap(imageBlob);
        const canvas = document.createElement("canvas");
        canvas.width = bitmap.width;
        canvas.height = bitmap.height;
        const ctx = canvas.getContext("2d")!;
        ctx.drawImage(bitmap, 0, 0);
        bitmap.close();

        applyEnhancements(canvas, ctx, options);

        const result = await new Promise<Blob | null>((resolve) => {
          canvas.toBlob(resolve, "image/png", 0.95);
        });

        return result;
      } catch (err) {
        const msg =
          err instanceof Error
            ? err.message
            : "Erreur lors de l'amélioration de l'image";
        setError(msg);
        return null;
      } finally {
        setIsProcessing(false);
      }
    },
    []
  );

  const autoEnhance = useCallback(
    (imageBlob: Blob) => enhance(imageBlob, AUTO_ENHANCE_SETTINGS),
    [enhance]
  );

  return { enhance, autoEnhance, isProcessing, error };
}
